import { currencyCodeSuffix } from '../../utils/money';
import { DateTime } from 'luxon';
import type { IServicePayoutBatchFieldsFragment } from '../../services/serviceService/__generated__/queries.generated';

/**
 * Weekly Provider payouts, paid by hand (spec 019 D23, W20, W24): a batch is
 * one Provider's confirmed jobs of a Monday-to-Sunday week (Phoenix time),
 * less refunds decided that week. Pure helpers; the server makes the batches.
 */

export type PayoutBatch = IServicePayoutBatchFieldsFragment;

export const PAYOUT_ZONE = 'America/Phoenix';

/** "Oct 5 – Oct 11, 2026". */
export function payoutWeekLabel(periodStart: string, periodEnd: string): string {
  const from = DateTime.fromISO(periodStart);
  const to = DateTime.fromISO(periodEnd);
  if (!from.isValid || !to.isValid) return `${periodStart} – ${periodEnd}`;
  return `${from.toFormat(from.year === to.year ? 'LLL d' : 'LLL d, yyyy')} – ${to.toFormat('LLL d, yyyy')}`;
}

/** The Monday (yyyy-MM-dd) of the week that holds `date` (a yyyy-MM-dd day). */
export function mondayOf(date: string): string | null {
  const day = DateTime.fromISO(date, { zone: PAYOUT_ZONE });
  return day.isValid ? day.startOf('week').toISODate() : null;
}

/** The last finished week's Monday, which is the newest batch (made Monday 00:10). */
export function lastPayoutWeek(now = Date.now()): string {
  return DateTime.fromMillis(now, { zone: PAYOUT_ZONE })
    .startOf('week')
    .minus({ weeks: 1 })
    .toISODate()!;
}

export function payoutBatchState(batch: Pick<PayoutBatch, 'paidAt' | 'amount'>): {
  label: string;
  tone: 'success' | 'warning' | 'neutral';
} {
  if (batch.paidAt) return { label: 'Paid', tone: 'success' };
  if (batch.amount === 0) return { label: 'Nothing to send', tone: 'neutral' };
  if (batch.amount < 0) return { label: 'Refunds were more than the jobs', tone: 'neutral' };
  return { label: 'Not paid yet', tone: 'warning' };
}

export function payoutCsvFileName(periodStart: string): string {
  return `service-payouts-${periodStart}.csv`;
}

export function payoutTotals(batches: readonly Pick<PayoutBatch, 'amount' | 'paidAt'>[]): {
  total: number;
  unpaid: number;
  unpaidCount: number;
} {
  const cents = (n: number) => Math.round(n * 100);
  let total = 0;
  let unpaid = 0;
  let unpaidCount = 0;
  for (const b of batches) {
    total += cents(b.amount);
    if (!b.paidAt && b.amount > 0) {
      unpaid += cents(b.amount);
      unpaidCount += 1;
    }
  }
  return { total: total / 100, unpaid: unpaid / 100, unpaidCount };
}

/** Saves text as a file in the browser (the payouts CSV). */
export function downloadTextFile(fileName: string, text: string, type = 'text/csv') {
  const blob = new Blob([text], { type: `${type};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * "$1,240.50", "-$12.00" for a week where refunds were more than the jobs;
 * "$1,240.50 CAD" on a CAD brand (spec 028 D14).
 */
export function payoutMoney(amount: number, currency?: string | null): string {
  const text = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${amount < 0 ? '-' : ''}$${text}${currencyCodeSuffix(currency)}`;
}

/** The Mondays of the last `count` finished weeks, newest first, for the week picker. */
export function recentPayoutWeeks(count = 12, now = Date.now()): string[] {
  const newest = DateTime.fromISO(lastPayoutWeek(now), { zone: PAYOUT_ZONE });
  return Array.from({ length: count }, (_, i) => newest.minus({ weeks: i }).toISODate()!);
}

/** "Oct 5 – Oct 11, 2026" from the Monday alone. */
export function payoutWeekLabelFromMonday(periodStart: string): string {
  const monday = DateTime.fromISO(periodStart);
  if (!monday.isValid) return periodStart;
  return payoutWeekLabel(periodStart, monday.plus({ days: 6 }).toISODate()!);
}
